import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import Bugsnag from "@bugsnag/js";
import { getQueryParams } from "@/utils/httpRequest";
import { DEFAULT_NOTIFICATION_PAYLOAD, NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { ANALYTIC_EVENTS, sendAnalytics, sendCollectionToAnalytics } from "@/utils/analytics";
import { baseUrl, onPrepareHeaders } from "@/redux/services/helpers";
import { showNotification } from "@/redux/actions/notification";
import { LIST_SERVICE_TAG } from "@/shared/store/service/commonServiceTags";
import { lenderBoardService } from "@/features/LenderBoard/Management/store/lenderBoardService";
import { setProcessingState } from "@/redux/reducers/modalConfirmation";

export const lenderBoardChatService = createApi({
  reducerPath: "lenderBoardChatApi",
  tagTypes: ["LenderBoardMessage"],
  baseQuery: fetchBaseQuery({
    baseUrl,
    prepareHeaders: onPrepareHeaders,
  }),
  endpoints: builder => ({
    getMessages: builder.infiniteQuery({
      query: (queryArg, api = {}) => {
        // Handle the nested queryArg structure from RTK Query infiniteQuery
        const actualArgs = queryArg.queryArg || queryArg;

        // RTK Query passes pageParam in the top-level queryArg for subsequent calls
        const pageParam = queryArg.pageParam || api.pageParam || 1;

        const { loanId, lenderBoardId, ...params } = actualArgs;

        // Remove pageParam and queryArg from params to avoid URL pollution
        const cleanParams = { ...params };
        delete cleanParams.pageParam;
        delete cleanParams.queryArg;

        const queryString = getQueryParams({ ...cleanParams, page: pageParam });
        const finalUrl = `loans/${loanId}/lender-boards/${lenderBoardId}/messages?${queryString}`;

        return finalUrl;
      },
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (lastPage, allPages, lastPageParam) => {
          if (!lastPage) {
            return undefined;
          }

          // Use lastPageParam instead of API's pageNumber since API seems to always return pageNumber: 1
          const currentPage = lastPageParam || lastPage.pageNumber || 1;
          const { totalPages } = lastPage;

          if (!totalPages) {
            return undefined;
          }

          // Stop if we've reached the total pages or if the last page has no items
          if (currentPage >= totalPages || !lastPage.items?.length) {
            return undefined;
          }

          const nextPage = currentPage + 1;

          return nextPage;
        },
      },
      providesTags: result =>
        result
          ? [
              ...(result.pages?.flatMap(
                page =>
                  page.items?.map(({ lenderBoardMessageId }) => ({
                    type: "LenderBoardMessage",
                    id: lenderBoardMessageId,
                  })) || [],
              ) || []),
              { type: "LenderBoardMessage", id: LIST_SERVICE_TAG },
            ]
          : [{ type: "LenderBoardMessage", id: LIST_SERVICE_TAG }],
    }),

    createMessage: builder.mutation({
      query: ({ loanId, lenderBoardId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/messages`,
        method: "POST",
        body,
      }),
      invalidatesTags: () => [{ type: "LenderBoardMessage", id: LIST_SERVICE_TAG }],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_MESSAGE_CREATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Message sent successfully",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderBoardService.util.invalidateTags([{ type: "LenderBoard", id: queryArgs.lenderBoardId }]));
        } catch (error) {
          Bugsnag.notify(error);
        }
      },
    }),

    updateMessage: builder.mutation({
      query: ({ loanId, lenderBoardId, lenderBoardMessageId, ...body }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/messages/${lenderBoardMessageId}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { messageId }) => [
        { type: "LenderBoardMessage", id: messageId },
        { type: "LenderBoardMessage", id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;

          sendCollectionToAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_MESSAGE_UPDATED, data);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Message updated successfully",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderBoardService.util.invalidateTags([{ type: "LenderBoard", id: queryArgs.lenderBoardId }]));
        } catch (error) {
          Bugsnag.notify(error);
        }
      },
    }),

    deleteMessage: builder.mutation({
      query: ({ loanId, lenderBoardId, lenderBoardMessageId }) => ({
        url: `loans/${loanId}/lender-boards/${lenderBoardId}/messages/${lenderBoardMessageId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { messageId }) => [
        { type: "LenderBoardMessage", id: messageId },
        { type: "LenderBoardMessage", id: LIST_SERVICE_TAG },
      ],
      async onQueryStarted(queryArgs, { dispatch, queryFulfilled }) {
        try {
          dispatch(setProcessingState(true));
          await queryFulfilled;

          sendAnalytics(ANALYTIC_EVENTS.LENDER_BOARD_MESSAGE_DELETED, queryArgs);
          dispatch(
            showNotification({
              ...DEFAULT_NOTIFICATION_PAYLOAD,
              message: {
                ...DEFAULT_NOTIFICATION_PAYLOAD.message,
                description: "Message deleted successfully",
                options: {
                  variant: NOTIFICATION_VARIANTS.SUCCESS,
                },
              },
            }),
          );
          dispatch(lenderBoardService.util.invalidateTags([{ type: "LenderBoard", id: queryArgs.lenderBoardId }]));
          dispatch(setProcessingState(false));
        } catch (error) {
          Bugsnag.notify(error);
          dispatch(setProcessingState(false));
        }
      },
    }),
  }),
});

export const {
  useGetMessagesInfiniteQuery,
  useCreateMessageMutation,
  useUpdateMessageMutation,
  useDeleteMessageMutation,
} = lenderBoardChatService;
